import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactCollapsingTable from 'react-collapsing-table';
import classnames from 'classnames';
import debounce from "lodash.debounce";
import { Search, Pagination, Coach, Blinds, Description, Type, Stack, Score, Hand, Access } from './components';
import { Preloader } from '../../../../components/Preloader';
import './QuizzesTable.scss';

function getQuizzUrl(quizzes, id) {
  return quizzes.find((quizz) => quizz.id === id)?.url;
}

function getQuizzDescription(quizzes, id) {
  return quizzes.find((quizz) => quizz.id === id)?.description;
}

export function QuizzesTable({
  isLoading, showSearch = true, data, initSort = { column: null, direction: 'ascending' }, initSearch = '', initRowSize = 100, showSizeChanger = true, onChangeView, title
}) {
  const [page, setPage] = useState(1);
  const [length, setLength] = useState(initRowSize);
  const [sort, setSort] = useState(initSort);
  const [search, setSearch] = useState(initSearch);
  const total = data?.recordsFiltered || 0;
  const start = useMemo(() => (page - 1) * length, [page, length]);
  const to  = useMemo(() => Math.min(start + length, total), [start, length, total]);
  const tableRef = useRef();

  const handleChangeView = useCallback((page, pageSize, search, sort) => {
    onChangeView({
      start: (page - 1) * pageSize,
      length: pageSize,
      search,
      ...sort
    });
  }, [onChangeView]);

  const performSearch = useMemo(() => debounce((length, searchValue, sort) => {
    setPage(1);
    handleChangeView(1, length, searchValue, sort);

    //actid=52
    !!searchValue && window.analytics?.track("search:query", {
      "query": searchValue,
      "parent_object_title": "Quizzes"
    });
  }, 400), [handleChangeView]);

  const columns = [
    { accessor: 'id', label: '#', priorityLevel: 1, position: 1, minWidth: 100 },
    { accessor: 'access_lbl', label: 'Access', priorityLevel: 7, position: 2, minWidth: 100, CustomComponent: Access },
    { accessor: 'description', label: 'Description', priorityLevel: 5, position: 3, minWidth: 250, CustomComponent: Description },
    { accessor: 'coach_name', label: 'Coach', priorityLevel: 2, position: 3, minWidth: 55, CustomComponent: Coach },
    { accessor: 'cards', label: 'Hand', priorityLevel: 6, position: 3, minWidth: 70, CustomComponent: Hand },
    { accessor: 'type', label: 'Type', priorityLevel: 3, position: 4, minWidth: 60, CustomComponent: Type },
    { accessor: 'stack_chips', label: 'Stack', priorityLevel: 9, position: 5, minWidth: 100, CustomComponent: Stack },
    { accessor: 'blind_small', label: 'Blinds', priorityLevel: 10, position: 6, minWidth: 100, CustomComponent: Blinds },
    { accessor: 'position', label: 'Pos', priorityLevel: 8, position: 7, minWidth: 100 },
    { accessor: 'opponent_position', label: 'Opp Pos', priorityLevel: 11, position: 7, minWidth: 100 },
    { accessor: 'user_score', label: 'Score', priorityLevel: 4, position: 10, minWidth: 150, CustomComponent: Score }
  ];

  useEffect(() => {
    if(isLoading || !data) {
      return;
    }

    function rowClick(e) {
      if(e.target.nodeName === 'svg' && e.target.closest('td.id')) {
        return;
      }

      const id = e.currentTarget.querySelector('td.id')?.textContent;

      if(!id) {
        console.log('No id found');
        return;
      }

      const url = getQuizzUrl(data.data, id);
      const description = getQuizzDescription(data.data, id);

      if(!url) {
        console.log('No url found');
        return;
      }

      if(e.shiftKey || e.ctrlKey || e.metaKey) {
        window.open(url);
      } else {
        // actid=5
        window.analytics?.track("object:click", {
          "object": "quiz",
          "parent_object_title": title,
          "title": description,
          "url": url
        });
        window.location = url;
      }

      e.stopPropagation();
    }

    tableRef.current.querySelectorAll('table tbody tr').forEach((row) => {
      row.addEventListener('click', rowClick);
    });

    window.dispatchEvent(new Event('resize'));
  }, [isLoading, data, title]);

  function updateData(data) {
    setSort(data.sort);
    setPage(1);
    handleChangeView(1, length, search, data.sort);
  }

  function onChangePage(newPage, newPageSize) {
    // actid=6
    window.analytics?.track("button:click", {
      "button_name": "Change page",
      "parent_object_title": title,
      "title": `Page changed to ${newPage}`,
    });

    const pageResult = length === newPageSize ? newPage : Math.ceil((start + 1) / newPageSize);

    setPage(pageResult);
    setLength(newPageSize);
    handleChangeView(pageResult, newPageSize, search, sort);
  }

  function onChangeSearch(e) {
    const searchValue = e.target.value;

    setSearch(searchValue);
    performSearch(length, searchValue, sort);
  }

  return (
    <div className={classnames('QuizzesTable', { 'QuizzesTable_loading': isLoading })}>
      <div className="QuizzesTable__loader-wrap" ref={tableRef}>
        <div className="QuizzesTable__head">
          {showSearch && (
            <Search
              className="QuizzesTable__search"
              value={search}
              onChange={onChangeSearch}
            />
          )}
        </div>

        <ReactCollapsingTable
          rows={data?.data || {}}
          columns={columns}
          rowSize={length}
          column={sort.column}
          direction={sort.direction}
          updateData={updateData}
        />

        <div className="QuizzesTable__footer">
          <p className="QuizzesTable__count">Showing {start + 1} to {to} of {total} entries</p>

          <div>
            <Pagination
              current={page}
              pageSize={length}
              total={total}
              showSizeChanger={showSizeChanger}
              pageSizeOptions={[10, 25, 50, 100]}
              onChange={onChangePage}
            />
          </div>
        </div>

        {isLoading && (
          <div className="QuizzesTable__preloader">
            <Preloader />
          </div>
        )}
      </div>
    </div>
  );
}
